// website/src/lib/fieldMappingConfig.js

// Defines the paths to access specific attributes within each data source's vehicle object.
export const sourceFieldAccess = { // Added export
    VAUTO: {
        vin: "vin",
        stockNumber: "stockNumber",
        inventoryDate: "inventoryDate",
        purchaseStatus: "status.purchaseStatus",
        type: "newUsed",
        year: "modelYear",
        make: "make",
        model: "model",
        trim: "series",
        exteriorColor: "exterior.color",
        interiorColor: "interior.color", // Path for general interior color
        interiorMaterial: "interior.material", // New
        odometer: "odometer",
        recall: "status.recall",
        listPrice: ["prices.pendingPrice", "prices.listPrice"],
        cost: "prices.unitCost",
        invoice: "prices.invoice",
        msrp: "prices.msrp",
        basePrice: null,
        doorCount: "body.doorCount",
        bodyType: "body.type",
        bedStyle: "body.bedStyle",
        cabType: "body.cabStyle",
        wheelBase: "body.wheelbase",
        driveTrain: "drivetrain.type",
        transmission: "transmission.type", // General transmission type
        transmissionGearCount: "transmission.gearCount", // New
        engine: "engine.description", // General engine description
        engineDisplacementL: "engine.displacement", // New
        engineCylinderCount: "engine.cylinderCount", // New
        engineAspiration: "engine.aspiration", // New
        engineCamshaft: "engine.camshaft", // New
        engineFuelIntake: "engine.fuelIntake", // New
        fuelType: "engine.fuelType",
        cityMpg: "cityMpg",
        highwayMpg: "highwayMpg",
        isCertified: "isCertified", // New
        // CVI 2.1 only fields — no cross-source mapping added for this epic
        vinStatus: null,
        transitState: null,
        transitDetail: null,
        advertisingState: null,
        sourceConfigurationId: null,
        engineSize: null,
        modelCode: null,
        engineCode: null,
    },
    CVI: {
        vin: "dealerVehicle.vin",
        stockNumber: "inventoryVehicle.stockNumber",
        inventoryDate: "inventoryVehicle.dateInStock",
        purchaseStatus: "inventoryVehicle.purchaseStatus",
        type: "inventoryVehicle.inventoryType",
        year: "dealerVehicle.description.year",
        make: "dealerVehicle.description.make",
        model: "dealerVehicle.description.model.normalizedName",
        trim: "dealerVehicle.description.trim.normalizedName",
        exteriorColor: "dealerVehicle.colors.exterior.0.normalizedName",
        interiorColor: "dealerVehicle.colors.interior.0.normalizedName", // Path for general interior color
        interiorMaterial: "dealerVehicle.colors.interior.0.materialTypes.0", // New
        odometer: "inventoryVehicle.odometer.value",
        recall: "dealerVehicle.history.hasOpenRecall",
        listPrice: "inventoryVehicle.prices.listPrice.value",
        cost: "inventoryVehicle.costs.original.value",
        invoice: "inventoryVehicle.costs.invoice.value",
        msrp: "inventoryVehicle.prices.msrp.value",
        basePrice: "dealerVehicle.description.originalBaseMsrp.value",
        doorCount: "dealerVehicle.bodyDescription.passengerDoors",
        bodyType: "dealerVehicle.bodyDescription.primaryBodyStyle",
        bedStyle: "dealerVehicle.bodyDescription.bedLength",
        cabType: "dealerVehicle.bodyDescription.cabType",
        wheelBase: "dealerVehicle.technicalSpecifications.wheelBase.value",
        driveTrain: "dealerVehicle.powertrain.drivetrain.type",
        transmission: "dealerVehicle.powertrain.transmission.type", // General transmission type
        transmissionGearCount: "dealerVehicle.powertrain.transmission.gearCount", // New
        engine: "dealerVehicle.powertrain.engine.primaryDescription", // General engine description
        engineDisplacementL: "dealerVehicle.powertrain.engine.displacement.value", // New
        engineCylinderCount: "dealerVehicle.powertrain.engine.cylinderCount", // New
        engineAspiration: "dealerVehicle.powertrain.engine.aspiration", // New
        engineCamshaft: "dealerVehicle.powertrain.engine.camshaft", // New
        engineFuelIntake: "dealerVehicle.powertrain.engine.fuelIntake", // New
        fuelType: "dealerVehicle.powertrain.engine.fuelType",
        cityMpg: "dealerVehicle.technicalSpecifications.fuelEconomy.city.value",
        highwayMpg: "dealerVehicle.technicalSpecifications.fuelEconomy.highway.value",
        isCertified: "inventoryVehicle.certification.type", // New - assuming 'type' or similar field exists if certified
        // CVI 2.1 only fields — no cross-source mapping added for this epic
        vinStatus: null,
        transitState: null,
        transitDetail: null,
        advertisingState: null,
        sourceConfigurationId: null,
        engineSize: null,
        modelCode: null,
        engineCode: null,
    },
    // CVI 2.1 (Inventory Records Service). Paths are relative to the inventory record, which
    // carries one copy of the vehicle per incoming channel under `sourceVehicles`.
    // `sourceVehicle` is the copy the comparison view picked (see lib/cvi2SourceVehicle.js),
    // attached so these paths stay flat; `sourceConfigurationId` says which channel it came from.
    // The 4 fields below that map to the 2.1 renames (List Price -> discountedPrice,
    // Invoice -> invoicePrice, Cost -> totalCost) and Engine Code render blank until E49867 maps
    // the VinSolutions IOL source — they are wired, not gated.
    CVI2: {
        vin: "vin",
        stockNumber: "sourceVehicle.stockNumber",
        inventoryDate: "sourceVehicle.dateInStock",
        purchaseStatus: null, // confirmed gap: no CVI 2.1 equivalent, see Advertising Lifecycle State
        type: "sourceVehicle.type",
        year: "sourceVehicle.description.year",
        make: "sourceVehicle.description.make",
        model: "sourceVehicle.description.model",
        trim: "sourceVehicle.description.trim",
        exteriorColor: "sourceVehicle.description.exteriorColor",
        interiorColor: "sourceVehicle.description.interiorColor",
        interiorMaterial: null,
        odometer: "sourceVehicle.odometer",
        recall: null,
        listPrice: "sourceVehicle.pricingStack.discountedPrice",
        cost: "sourceVehicle.costStack.totalCost",
        invoice: "sourceVehicle.costStack.invoicePrice",
        msrp: "sourceVehicle.pricingStack.msrp",
        basePrice: "sourceVehicle.pricingStack.basePrice",
        doorCount: null,
        bodyType: "sourceVehicle.description.bodyStyle",
        bedStyle: null,
        cabType: null,
        wheelBase: null,
        driveTrain: null,
        transmission: null,
        transmissionGearCount: null,
        engine: null,
        engineDisplacementL: null,
        engineCylinderCount: null,
        engineAspiration: null,
        engineCamshaft: null,
        engineFuelIntake: null,
        fuelType: "sourceVehicle.description.fuelType",
        cityMpg: null,
        highwayMpg: null,
        isCertified: null,
        // CVI 2.1 only
        vinStatus: "vinStatus",
        transitState: "lifecycle.transitState",
        transitDetail: "lifecycle.transitDetail",
        advertisingState: "lifecycle.advertisingState",
        sourceConfigurationId: "sourceVehicle.sourceConfigurationId",
        engineSize: "sourceVehicle.description.engineSize",
        modelCode: "sourceVehicle.codes.modelCode",
        engineCode: "sourceVehicle.codes.engineCode",
    },
    ATC: { // Autotrader
        vin: "vin",
        stockNumber: "stockNumber",
        inventoryDate: "dateListed",
        purchaseStatus: null,
        type: "listingType",
        year: "year",
        make: "make.name",
        model: "model.name",
        trim: "trim.name",
        exteriorColor: "extColor.name",
        interiorColor: "intColor.name",
        interiorMaterial: null, // New
        odometer: "mileage",
        recall: "carProducts.0.vehicleHistoryReport.OPEN_RECALLS_REPORTED",
        listPrice: "primaryPrice",
        cost: null,
        invoice: null,
        msrp: "msrp",
        basePrice: null,
        doorCount: "doors.code",
        bodyType: "bodyStyles.0.name",
        bedStyle: null,
        cabType: null,
        wheelBase: null,
        driveTrain: "drive.name",
        transmission: "transmission.name",
        transmissionGearCount: null, // New
        engine: "engine.name",
        engineDisplacementL: null, // New
        engineCylinderCount: null, // New
        engineAspiration: null, // New
        engineCamshaft: null, // New
        engineFuelIntake: null, // New
        fuelType: "fuelType.name",
        cityMpg: "mpgCity",
        highwayMpg: "mpgHwy",
        isCertified: null, // New - ATC 'listingType' might be "Certified"
        // CVI 2.1 only fields — no cross-source mapping added for this epic
        vinStatus: null,
        transitState: null,
        transitDetail: null,
        advertisingState: null,
        sourceConfigurationId: null,
        engineSize: null,
        modelCode: null,
        engineCode: null,
    },
    DDC: {
        vin: "vin",
        stockNumber: "stockNumber",
        inventoryDate: "inventoryDate",
        purchaseStatus: null, // "status", DDC status doesn't map cleanly to purchase status, might need custom handling
        type: "type",
        year: "year",
        make: "make",
        model: "model.en_US",
        trim: "trim.en_US",
        exteriorColor: "exteriorColor",
        interiorColor: "normalInteriorColor.en_US",
        interiorMaterial: null, // New
        odometer: "odometer",
        recall: null,
        listPrice: "internetPrice",
        cost: "actualCost",
        invoice: "invoicePrice",
        msrp: "msrp",
        basePrice: null,
        doorCount: "doors.en_US",
        bodyType: "bodyStyle.en_US",
        bedStyle: null,
        cabType: null,
        wheelBase: null,
        driveTrain: "driveLine.en_US",
        transmission: "transmission.en_US",
        transmissionGearCount: null, // New
        engine: "engine",
        engineDisplacementL: null, // New
        engineCylinderCount: null, // New
        engineAspiration: null, // New
        engineCamshaft: null, // New
        engineFuelIntake: null, // New
        fuelType: "fuelType.en_US",
        cityMpg: "cityFuelEconomy.imperial",
        highwayMpg: "highwayFuelEconomy.imperial",
        isCertified: null, // New
        // CVI 2.1 only fields — no cross-source mapping added for this epic
        vinStatus: null,
        transitState: null,
        transitDetail: null,
        advertisingState: null,
        sourceConfigurationId: null,
        engineSize: null,
        modelCode: null,
        engineCode: null,
    },
    VINSOL: {
        vin: "core.vin",
        stockNumber: "core.stockNumber",
        inventoryDate: null,
        purchaseStatus: null,
        type: "core.inventoryType",
        year: "core.year",
        make: "core.make",
        model: "core.model",
        trim: "core.trim",
        exteriorColor: "core.exteriorColor",
        interiorColor: "core.interiorColor",
        interiorMaterial: null, // New
        odometer: "core.mileage",
        recall: null,
        listPrice: "pricing.internetPrice",
        cost: "pricing.cost",
        invoice: "pricing.invoicePrice",
        msrp: "pricing.msrp",
        basePrice: null,
        doorCount: null,
        bodyType: null,
        bedStyle: null,
        cabType: null,
        wheelBase: null,
        driveTrain: null,
        transmission: "core.transmission",
        transmissionGearCount: null, // New
        engine: "core.engine",
        engineDisplacementL: null, // New - "core.engine" is "Regular Unleaded I-4 2.0 L/122", might need parsing
        engineCylinderCount: null, // New - "core.engine" is "Regular Unleaded I-4 2.0 L/122", might need parsing
        engineAspiration: null, // New
        engineCamshaft: null, // New
        engineFuelIntake: null, // New
        fuelType: null,
        cityMpg: "core.cityMPG",
        highwayMpg: "core.hwyMPG",
        isCertified: "condition.isCertified", // New - Assuming a path like this might exist
        // CVI 2.1 only fields — no cross-source mapping added for this epic
        vinStatus: null,
        transitState: null,
        transitDetail: null,
        advertisingState: null,
        sourceConfigurationId: null,
        engineSize: null,
        modelCode: null,
        engineCode: null,
    }
    // Add other sources here, e.g., MVS, EWS, etc.
    // MVS: {
    //     vin: "vehicleIdentification.vin",
    //     stockNumber: "dealerInfo.stockNumber",
    //     // ... and so on for all attributes
    // }
};

// Defines the structure of the comparison table: categories, fields, and their display names.
// The 'attribute' key links to the keys in sourceFieldAccess.
export const commonFieldsDefinition = [ // Added export
    { displayName: "Pricing Information", isCategory: true },
    // In CVI 2.1 these three resolve to the renamed fields (discountedPrice / totalCost /
    // invoicePrice) — each is a single 2.1 field surfaced once, not duplicated below.
    { displayName: "List Price / Discounted Price", attribute: "listPrice" },
    { displayName: "Cost / Total Cost", attribute: "cost" },
    { displayName: "Invoice / Invoice Price", attribute: "invoice" },
    { displayName: "MSRP", attribute: "msrp" },
    // CVI 1.0 originalBaseMsrp.value /CVI 2.1 pricingStack.basePrice. Added beside MSRP, not in place of it.
    { displayName: "Base Price", attribute: "basePrice" },
    { isSpacing: true },

    { displayName: "Vehicle Description", isCategory: true },
    { displayName: "VIN", attribute: "vin" },
    { displayName: "Stock #", attribute: "stockNumber" },
    { displayName: "Type", attribute: "type" },
    { displayName: "Purchase Status", attribute: "purchaseStatus" },
    { displayName: "Year", attribute: "year" },
    { displayName: "Make", attribute: "make" },
    { displayName: "Model", attribute: "model" },
    { displayName: "Trim", attribute: "trim" },
    { displayName: "Odometer", attribute: "odometer" },
    // Other fields excluded by US1914626
    { isSpacing: true },

    // CVI 2.1 field scope (E54114). The first four rows already had mappings in the other sources;
    // Engine Size / Model Code / Engine Code are 2.1-only and render blank elsewhere. No new
    // cross-source mappings were added for this epic.
    { displayName: "Vehicle Identification", isCategory: true },
    { displayName: "Body Style", attribute: "bodyType" },
    { displayName: "Exterior Color", attribute: "exteriorColor" },
    { displayName: "Interior Color", attribute: "interiorColor" },
    { displayName: "Fuel Type", attribute: "fuelType" },
    { displayName: "Engine Size", attribute: "engineSize" },
    { displayName: "Model Code", attribute: "modelCode" },
    { displayName: "Engine Code", attribute: "engineCode" },
    { isSpacing: true },

    { displayName: "CVI 2.1 Record", isCategory: true },
    { displayName: "VIN Status", attribute: "vinStatus" },
    { displayName: "Transit Lifecycle State", attribute: "transitState" },
    { displayName: "Transit Detail", attribute: "transitDetail" },
    { displayName: "Advertising Lifecycle State", attribute: "advertisingState" },
    { displayName: "Source Configuration ID", attribute: "sourceConfigurationId" },
];

// Export a list of available sources for dropdowns.
// Keys should match those in sourceFieldAccess.
export const sources = {
    VAUTO: "vAuto",
    // CVI 1.0 (Sellable Vehicle Service) and CVI 2.1 (Inventory Records Service) are distinct,
    // coexisting systems — every representation is labeled with the system that served it.
    CVI: "CVI 1.0",
    CVI2: "CVI 2.1",
    ATC: "Autotrader", // Added Autotrader
    DDC: "Dealer.com", // Added DDC
    VINSOL: "VinSolutions", // Added VinSolutions
    // MVS: "MVS", // Uncomment and add more sources as they are defined in sourceFieldAccess
};

// Generates the fieldMappings array needed by the DataCompare component.
export function getFieldMappings(source1Key, source2Key) {
    if (!sourceFieldAccess[source1Key] || !sourceFieldAccess[source2Key]) {
        console.error(`Invalid source key(s) provided for field mappings: ${source1Key}, ${source2Key}`);
        return [];
    }

    const s1Access = sourceFieldAccess[source1Key];
    const s2Access = sourceFieldAccess[source2Key];

    return commonFieldsDefinition.map(fieldDef => {
        if (fieldDef.isCategory || fieldDef.isSpacing) {
            return { ...fieldDef }; // Pass through category and spacing rows
        }

        // For data rows, construct the paths based on the selected sources
        const path1 = s1Access[fieldDef.attribute];
        const path2 = s2Access[fieldDef.attribute];

        return {
            displayName: fieldDef.displayName,
            paths: [path1, path2], // GetValue in DataCompare will handle undefined paths
            isDate: fieldDef.isDate || false,
        };
    });
}
